import { useEffect, useState, type ChangeEvent, type FormEvent, type MouseEvent } from "react";
import { FileUp, Loader2, X } from "lucide-react";
import type { IsoCertificateCode, IsoCertificateSlot } from "../types/certificados-iso";
import "../styles/certificados-iso.css";

type Props = {
  slot: IsoCertificateSlot | null;
  uploadingCode: IsoCertificateCode | null;
  error: string | null;
  onClose: () => void;
  onSubmit: (slot: IsoCertificateSlot, file: File) => Promise<void>;
};

export function IsoCertificateUploadModal({
  slot,
  uploadingCode,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    setFile(null);
  }, [slot]);

  useEffect(() => {
    if (!slot) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && uploadingCode === null) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, slot, uploadingCode]);

  if (!slot) {
    return null;
  }

  const submitting = uploadingCode === slot.codigo;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (file && !submitting) {
      void onSubmit(slot, file);
    }
  };

  return (
    <div
      className="iso-certificates-modal-backdrop"
      role="presentation"
      onMouseDown={() => {
        if (!submitting) {
          onClose();
        }
      }}
    >
      <form
        className="iso-certificates-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="iso-certificate-upload-title"
        onSubmit={submit}
        onMouseDown={(event: MouseEvent<HTMLFormElement>) => event.stopPropagation()}
      >
        <header className="iso-certificates-modal-header">
          <div className="iso-certificates-modal-copy">
            <span>Gestión de certificado</span>
            <h2 id="iso-certificate-upload-title">
              {slot.available ? `Sustituir ${slot.label}` : `Añadir ${slot.label}`}
            </h2>
            <p>
              El archivo vigente no se elimina hasta que el nuevo PDF se haya guardado correctamente.
            </p>
          </div>
          <button
            type="button"
            className="iso-certificates-modal-icon"
            aria-label="Cerrar"
            onClick={onClose}
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </header>

        <div className="iso-certificates-modal-body iso-certificates-upload-form">
          <label className="iso-certificates-field">
            <span>Archivo PDF</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              disabled={submitting}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setFile(event.target.files?.[0] ?? null);
              }}
            />
            <small>Solo PDF. Tamaño máximo: 50 MB.</small>
          </label>

          {slot.documento ? (
            <>
              <div className="iso-certificates-current-file">
                <span>Archivo actual</span>
                <strong>{slot.documento.nombre_original}</strong>
              </div>
              <div className="iso-certificates-replacement-notice">
                El PDF seleccionado sustituirá este archivo cuando la nueva carga finalice correctamente.
              </div>
            </>
          ) : null}

          {error ? <div className="iso-certificates-form-error">{error}</div> : null}
        </div>

        <footer className="iso-certificates-modal-footer">
          <button
            type="button"
            className="iso-certificates-secondary-button"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="iso-certificates-primary-button"
            disabled={file === null || submitting}
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <FileUp size={16} />}
            {slot.available ? "Sustituir certificado" : "Subir certificado"}
          </button>
        </footer>
      </form>
    </div>
  );
}
